import React, { useEffect, useMemo, useState, useRef } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Checkbox } from "../../components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Users, Car as CarIcon, CalendarDays, Search, AlertTriangle, CheckCircle2, Layers, Plus, Send, X, Trash2 } from "lucide-react";
import html2canvas from "html2canvas";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import {
  DAY_NAMES, MONTH_NAMES,
  COLORS, PROJECT_INACTIVE_CATEGORIES, DISMANTLE_WINDOW_DAYS, INVENTORY_SET_COLORS,
  toISO, mondayOf, addDays, fmtRoDay, dowFromIso, isSaturdayIso, fmtRangeLabel,
} from "./utils";

/* eslint-disable no-unused-vars */

export default function ShareDayDialog({ day, entries, personsIndex, carsIndex, projectsIndex = {}, regiiIndex = {}, regiiOrder = [], projectsOrder = [], freePersons = [], company, logoDataUrl, onClose }) {
  const projectsRef = React.useRef(null);
  const regiiRef = React.useRef(null);
  const [busy, setBusy] = useState(false);
  const [activeTab, setActiveTab] = useState("projects"); // projects | regii
  // Ultimul control lichide per mașină (cine + când) — afișat sub fiecare mașină alocată
  const [liquidsMap, setLiquidsMap] = useState({});
  useEffect(() => {
    api.get("/me/car-maintenance/summary").then((r) => {
      const m = {};
      (r.data || []).forEach((row) => {
        if (row.type === "liquids" && row.car_id) m[row.car_id] = { ts: row.last_ts, user: row.last_user };
      });
      setLiquidsMap(m);
    }).catch(() => {});
  }, []);

  // Build a human-readable date label: "Luni, 27 aprilie 2026"
  const dObj = new Date(day + "T00:00:00");
  const pretty = `${DAY_NAMES[(dObj.getDay() + 6) % 7]}, ${dObj.getDate()} ${MONTH_NAMES[dObj.getMonth()]} ${dObj.getFullYear()}`;

  // Build rank maps (id → index în lista din Planificator) ca să sortăm entries
  // în AceEAȘI ordine cu chip-urile din CreateDayDialog/Planificator.
  const regieRank = useMemo(() => {
    const m = new Map();
    (regiiOrder || []).forEach((id, idx) => m.set(id, idx));
    return m;
  }, [regiiOrder]);
  const projectRank = useMemo(() => {
    const m = new Map();
    (projectsOrder || []).forEach((id, idx) => m.set(id, idx));
    return m;
  }, [projectsOrder]);

  // Split entries — apoi sortează după rank-ul din Planificator (ordine identică cu chip-urile la „+")
  const projectEntries = useMemo(() => {
    const arr = entries.filter((e) => e.project_id || (!e.regie_id && !e.is_regie));
    return arr.slice().sort((a, b) => {
      const ra = projectRank.has(a.project_id) ? projectRank.get(a.project_id) : 9999;
      const rb = projectRank.has(b.project_id) ? projectRank.get(b.project_id) : 9999;
      if (ra !== rb) return ra - rb;
      return (a.label || "").localeCompare(b.label || "", "ro");
    });
  }, [entries, projectRank]);
  const regieEntries = useMemo(() => {
    const arr = entries.filter((e) => e.regie_id || e.is_regie);
    return arr.slice().sort((a, b) => {
      const ra = regieRank.has(a.regie_id) ? regieRank.get(a.regie_id) : 9999;
      const rb = regieRank.has(b.regie_id) ? regieRank.get(b.regie_id) : 9999;
      if (ra !== rb) return ra - rb;
      return (a.label || "").localeCompare(b.label || "", "ro");
    });
  }, [entries, regieRank]);

  // Get address per entry from indexes
  const addrFor = (e) => {
    if (e.project_id && projectsIndex[e.project_id]?.adresa) return projectsIndex[e.project_id].adresa;
    if (e.regie_id && regiiIndex[e.regie_id]?.adresa) return regiiIndex[e.regie_id].adresa;
    return null;
  };
  const firmaFor = (e) => {
    if (e.regie_id && regiiIndex[e.regie_id]?.firma) return regiiIndex[e.regie_id].firma;
    return null;
  };
  const vorarbeiterFor = (e) => {
    if (e.regie_id && regiiIndex[e.regie_id]?.vorarbeiter) return regiiIndex[e.regie_id].vorarbeiter;
    return null;
  };
  // Detalii suplimentare proiect (zone EKAS/Factsheet, work_zones, special, saci)
  const projectMetaFor = (e) => {
    if (!e.project_id) return null;
    return projectsIndex[e.project_id] || null;
  };

  // 🔀 Itinerariu multi-șantier: pid → opriri {entryId, label, from, to} ordonate după ora din planificator
  const personItinerary = useMemo(() => {
    const stops = {};
    let idx = 0;
    for (const e of [...projectEntries, ...regieEntries]) {
      const splits = (e.person_splits || {})[day] || {};
      for (const pid of (e.person_ids || [])) {
        (stops[pid] = stops[pid] || []).push({
          entryId: e.id,
          label: e.label,
          from: splits[pid]?.from || null,
          to: splits[pid]?.to || null,
          idx,
        });
      }
      idx++;
    }
    for (const list of Object.values(stops)) {
      list.sort((a, b) => {
        if (a.from && b.from) return a.from.localeCompare(b.from);
        if (a.from) return -1;
        if (b.from) return 1;
        return a.idx - b.idx;
      });
    }
    return stops;
  }, [projectEntries, regieEntries, day]);

  const generateBlob = async (kind /* "projects" | "regii" */) => {
    const el = kind === "projects" ? projectsRef.current : regiiRef.current;
    if (!el) return null;
    const canvas = await html2canvas(el, {
      backgroundColor: "#ffffff",
      scale: 2,
      useCORS: true,
      logging: false,
    });
    return await new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png", 0.95));
  };

  const downloadOne = async (kind) => {
    setBusy(true);
    try {
      const blob = await generateBlob(kind);
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `planificator-${kind}-${day}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Imagine ${kind === "projects" ? "proiecte" : "regii"} descărcată`);
    } catch (e) { toast.error("Eroare la generare imagine"); }
    finally { setBusy(false); }
  };

  const downloadBoth = async () => {
    setBusy(true);
    try {
      // Render proiecte
      if (projectEntries.length > 0) {
        setActiveTab("projects");
        await new Promise((r) => setTimeout(r, 150));
        const b1 = await generateBlob("projects");
        if (b1) {
          const url = URL.createObjectURL(b1);
          const a = document.createElement("a");
          a.href = url; a.download = `planificator-proiecte-${day}.png`;
          document.body.appendChild(a); a.click(); a.remove();
          URL.revokeObjectURL(url);
        }
      }
      if (regieEntries.length > 0) {
        setActiveTab("regii");
        await new Promise((r) => setTimeout(r, 150));
        const b2 = await generateBlob("regii");
        if (b2) {
          const url = URL.createObjectURL(b2);
          const a = document.createElement("a");
          a.href = url; a.download = `planificator-regii-${day}.png`;
          document.body.appendChild(a); a.click(); a.remove();
          URL.revokeObjectURL(url);
        }
      }
      toast.success("Ambele imagini descărcate");
    } catch (e) { toast.error("Eroare la descărcare"); }
    finally { setBusy(false); }
  };

  const shareWhatsApp = async (kind) => {
    setBusy(true);
    try {
      const blob = await generateBlob(kind);
      if (!blob) {
        toast.error("Nu s-a putut genera imaginea");
        return;
      }
      const file = new File([blob], `planificator-${kind}-${day}.png`, { type: "image/png" });
      const shareText = `Planificare ${kind === "projects" ? "proiecte" : "regii"} ${pretty}`;
      // Încearcă direct navigator.share (fără canShare — pe iOS Safari canShare
      // returnează uneori false chiar dacă share-ul cu fișiere funcționează).
      let sharedOk = false;
      if (navigator.share) {
        try {
          await navigator.share({ files: [file], title: shareText, text: shareText });
          sharedOk = true;
          toast.success("Partajat cu succes");
        } catch (err) {
          if (err?.name === "AbortError") {
            // User cancelled — nu e eroare
            sharedOk = true;
          } else {
            // Log ceva util în consolă pentru debug (Tudor poate să trimită screen)
            console.warn("[ShareWhatsApp] navigator.share failed:", err?.name, err?.message);
          }
        }
      }
      if (!sharedOk) {
        // Fallback: descarcă imaginea + deschide wa.me cu text; utilizatorul atașează manual
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `planificator-${kind}-${day}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank");
        toast.info("📎 Imaginea a fost descărcată. Atașează-o manual în WhatsApp.", { duration: 6000 });
      }
    } catch (e) {
      console.error("[ShareWhatsApp] unexpected error:", e);
      toast.error(`Eroare la partajare: ${e?.message || "necunoscută"}`);
    } finally { setBusy(false); }
  };

  // Helper componentă: renderează un card întreg pentru un set de entries
  const renderCard = (ref, title, badge, items, accentColor) => (
    <div ref={ref} style={{ width: "440px", background: "#ffffff", fontFamily: "Inter, Arial, sans-serif" }}>
      <div style={{ background: "#0B1120", color: "#fff", padding: "14px 16px", display: "flex", alignItems: "center", gap: "12px" }}>
        {logoDataUrl && (
          <img src={logoDataUrl} alt="" crossOrigin="anonymous" style={{ width: "44px", height: "44px", borderRadius: "50%", objectFit: "cover", border: "2px solid #fff" }}/>
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: "16px", letterSpacing: "0.3px" }}>{company?.name || "ConVic GmbH"}</div>
          <div style={{ fontSize: "10px", opacity: 0.8, textTransform: "uppercase", letterSpacing: "1px" }}>
            <span style={{ background: accentColor, color: "#fff", padding: "1px 6px", borderRadius: "3px", marginRight: "5px", fontWeight: 800 }}>{badge}</span>
            {title} · Planificare zilnică
          </div>
        </div>
        <div style={{ textAlign: "right", fontSize: "11px", fontWeight: 600 }}>
          <div>{pretty}</div>
        </div>
      </div>
      <div style={{ padding: "14px", display: "flex", flexDirection: "column", gap: "10px", background: "#F8FAFC" }}>
        {items.map((e) => {
          const names = (e.person_ids || []).map((id) => personsIndex[id]).filter(Boolean);
          const cars = (e.car_ids || []).map((id) => {
            const c = carsIndex[id];
            if (!c) return null;
            return typeof c === "string" ? { label: c, _id: id } : { ...c, _id: id };
          }).filter(Boolean);
          const adresa = addrFor(e);
          const firma = firmaFor(e);
          const vorarbeiter = vorarbeiterFor(e);
          const pmeta = projectMetaFor(e);
          const tool = e.allocation_color ? INVENTORY_SET_COLORS.find((t) => t.code === e.allocation_color) : null;
          // Pregătim chip-uri pe zone (EKAS / Factsheet / Merkblatt / Altele)
          const typeColors = {
            "EKAS":      { bg: "#FEE2E2", border: "#FCA5A5", text: "#7F1D1D" },
            "Factsheet": { bg: "#DBEAFE", border: "#93C5FD", text: "#1E3A8A" },
            "Merkblatt": { bg: "#FEF3C7", border: "#FCD34D", text: "#78350F" },
            "Altele":    { bg: "#F1F5F9", border: "#CBD5E1", text: "#334155" },
          };
          return (
            <div key={e.id} style={{
              border: `2px solid ${e.color || accentColor}`,
              borderRadius: "10px",
              background: "#fff",
              overflow: "hidden",
            }}>
              <div style={{
                background: e.color || accentColor,
                color: "#fff",
                padding: "10px 12px",
              }}>
                <div style={{ fontWeight: 800, fontSize: "14px", letterSpacing: "0.2px", lineHeight: 1.3 }}>
                  {e.label}
                  {firma && <span style={{ marginLeft: "6px", fontSize: "9px", background: "rgba(255,255,255,0.25)", padding: "2px 6px", borderRadius: "4px", fontWeight: 700 }}>{firma}</span>}
                </div>
                {adresa && (
                  <div style={{ marginTop: "4px", fontSize: "11px", fontWeight: 600, opacity: 0.95, lineHeight: 1.35, display: "flex", alignItems: "flex-start", gap: "4px" }}>
                    <span style={{ fontSize: "11px" }}>📍</span>
                    <span>{adresa}</span>
                  </div>
                )}
                {vorarbeiter && (
                  <div style={{ marginTop: "4px", fontSize: "11px", fontWeight: 700, lineHeight: 1.35, display: "flex", alignItems: "flex-start", gap: "4px", background: "rgba(255,255,255,0.2)", padding: "3px 7px", borderRadius: "4px" }} data-testid="share-regie-vorarbeiter">
                    <span style={{ fontSize: "11px" }}>👷</span>
                    <span>Șef echipă: {vorarbeiter}</span>
                  </div>
                )}
                {tool ? (
                  <div style={{ marginTop: "5px", display: "inline-flex", alignItems: "center", gap: "5px", background: "#ffffff", border: `2.5px solid ${tool.hex}`, borderRadius: "5px", padding: "3px 9px", fontSize: "11.5px", fontWeight: 800, color: tool.hex, letterSpacing: "0.3px" }} data-testid={`share-tool-${e.id}`}>
                    🧰 SCULE: {tool.emoji} {tool.label.toUpperCase()}
                  </div>
                ) : e.project_id ? (
                  <div style={{ marginTop: "5px", display: "inline-flex", alignItems: "center", gap: "5px", background: "#FEF2F2", border: "2px dashed #DC2626", borderRadius: "5px", padding: "3px 9px", fontSize: "11px", fontWeight: 800, color: "#DC2626" }} data-testid={`share-tool-missing-${e.id}`}>
                    ⚠️ FĂRĂ SCULE ALOCATE
                  </div>
                ) : null}
              </div>
              <div style={{ padding: "10px 12px", display: "grid", gridTemplateColumns: "1fr", gap: "8px" }}>
                {pmeta && (Array.isArray(pmeta.zones) && pmeta.zones.length > 0) && (
                  <div>
                    <div style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#64748B", marginBottom: "5px" }}>
                      🗂️ Zone proiect ({pmeta.zones.reduce((sum, z) => sum + (z.count || 1), 0)} buc)
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginBottom: "5px" }}>
                      {pmeta.zones.map((z, idx) => {
                        const c = typeColors[z.type] || typeColors["Altele"];
                        return (
                          <span key={idx} style={{
                            background: c.bg, border: `1px solid ${c.border}`, color: c.text,
                            padding: "2px 7px", borderRadius: "999px", fontSize: "10.5px", fontWeight: 700,
                          }}>
                            {z.label || z.type}
                          </span>
                        );
                      })}
                    </div>
                    {pmeta.zones.some((z) => z.description) && (
                      <div style={{ fontSize: "11px", color: "#475569", lineHeight: 1.45 }}>
                        {pmeta.zones.filter((z) => z.description).map((z, idx) => (
                          <div key={idx} style={{ marginTop: "2px" }}>
                            <strong style={{ color: (typeColors[z.type] || typeColors["Altele"]).text }}>
                              {z.label || z.type}:
                            </strong>{" "}{z.description}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {pmeta && pmeta.work_zones && pmeta.work_zones.trim() && (
                  <div>
                    <div style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#64748B", marginBottom: "4px" }}>📐 Lucrări de făcut</div>
                    <div style={{ fontSize: "11.5px", color: "#1E293B", lineHeight: "1.5", whiteSpace: "pre-wrap", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "4px", padding: "6px 8px" }}>
                      {pmeta.work_zones.trim()}
                    </div>
                  </div>
                )}
                {pmeta && pmeta.utilities && ["wasser", "strom", "zugang"].some((k) => pmeta.utilities[k]?.checked || (pmeta.utilities[k]?.text || "").trim()) && (
                  <div data-testid={`share-utilities-${e.id}`}>
                    <div style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#64748B", marginBottom: "4px" }}>🔌 Wasser / Strom / Zugang</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
                      {[
                        { key: "wasser", label: "Wasser", emoji: "💧" },
                        { key: "strom", label: "Strom", emoji: "⚡" },
                        { key: "zugang", label: "Zugang", emoji: "🚪" },
                      ].map((it) => {
                        const u = pmeta.utilities[it.key] || {};
                        const txt = (u.text || "").trim();
                        if (!u.checked && !txt) return null;
                        const isZugang = it.key === "zugang";
                        return (
                          <div key={it.key} style={{
                            fontSize: "11.5px", lineHeight: 1.4, padding: "4px 8px", borderRadius: "4px",
                            background: isZugang ? "#FFF7ED" : "#F8FAFC",
                            border: `1px solid ${isZugang ? "#FED7AA" : "#E2E8F0"}`,
                            color: "#1E293B",
                          }}>
                            <strong>{it.emoji} {it.label}:</strong>{" "}
                            <span style={{ fontWeight: 800, color: u.checked ? "#059669" : "#DC2626" }}>{u.checked ? "✓" : "✗"}</span>
                            {txt && <span style={{ fontWeight: isZugang ? 700 : 500 }}> — {txt}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {pmeta && ((pmeta.bags_festgebunden || 0) > 0 || (pmeta.bags_schwachgebunden || 0) > 0 || pmeta.requires_post_measurement || pmeta.is_ruckbau) && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                    {(pmeta.bags_festgebunden || 0) > 0 && (
                      <span style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B", padding: "2px 7px", borderRadius: "4px", fontSize: "10.5px", fontWeight: 700 }}>
                        🛍 Festgebunden: {pmeta.bags_festgebunden}
                      </span>
                    )}
                    {(pmeta.bags_schwachgebunden || 0) > 0 && (
                      <span style={{ background: "#FFF7ED", border: "1px solid #FED7AA", color: "#9A3412", padding: "2px 7px", borderRadius: "4px", fontSize: "10.5px", fontWeight: 700 }}>
                        🛍 Schwachgebunden: {pmeta.bags_schwachgebunden}
                      </span>
                    )}
                    {pmeta.requires_post_measurement && (
                      <span style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", color: "#1E40AF", padding: "2px 7px", borderRadius: "4px", fontSize: "10.5px", fontWeight: 700 }}>
                        🌬️ Luftmessung obligatorie
                      </span>
                    )}
                    {pmeta.is_ruckbau && (
                      <span style={{ background: "#F5F3FF", border: "1px solid #DDD6FE", color: "#5B21B6", padding: "2px 7px", borderRadius: "4px", fontSize: "10.5px", fontWeight: 700 }}>
                        🏗 Rückbau
                      </span>
                    )}
                  </div>
                )}
                {pmeta && pmeta.special && pmeta.special.trim() && (
                  <div style={{
                    background: "#FEF2F2",
                    border: "1.5px solid #F87171",
                    borderLeft: "5px solid #DC2626",
                    borderRadius: "4px",
                    padding: "7px 9px",
                    fontSize: "11.5px",
                    color: "#7F1D1D",
                    fontWeight: 600,
                    lineHeight: "1.45",
                    whiteSpace: "pre-wrap",
                  }}>
                    <div style={{ fontSize: "9.5px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.5px", color: "#DC2626", marginBottom: "2px" }}>⚠️ Note speciale</div>
                    {pmeta.special.trim()}
                  </div>
                )}
                <div>
                  <div style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#64748B", marginBottom: "4px" }}>🚗 Autoturisme</div>
                  {cars.length > 0 ? (
                    <div style={{ fontSize: "12px", color: "#1E293B", lineHeight: "1.5" }}>
                      {cars.map((c, i) => {
                        const label = typeof c === "string" ? c : (c?.label || "");
                        const carObj = typeof c === "object" && c ? c : null;
                        const activeDefects = (carObj?.defects || []).filter((d) => !d.fixed);
                        const defectLines = [
                          ...activeDefects.map((d) => (d.reason || "").trim()).filter(Boolean),
                          (carObj && carObj.status === "defect" && carObj.defect_reason ? (carObj.defect_reason || "").trim() : ""),
                        ].filter(Boolean);
                        const hasDefect = defectLines.length > 0 || carObj?.status === "defect";
                        return (
                          <div key={i} style={{ marginBottom: defectLines.length ? "4px" : "2px" }}>
                            <div>{hasDefect ? "⚠️ " : "• "}{label}{carObj?.status === "defect" ? " (DEFECT)" : ""}</div>
                            {(() => {
                              const lq = carObj?._id ? liquidsMap[carObj._id] : null;
                              if (!lq) {
                                return (
                                  <div style={{ marginLeft: "12px", fontSize: "10px", fontWeight: 700, color: "#B45309" }} data-testid="share-car-liquids-missing">
                                    🛢️ Lichide: NEVERIFICATE
                                  </div>
                                );
                              }
                              const d = new Date(lq.ts);
                              const dateStr = `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
                              const ageDays = (new Date(day + "T00:00:00") - new Date(lq.ts.slice(0, 10) + "T00:00:00")) / 86400000;
                              const stale = ageDays > 7;
                              return (
                                <div style={{ marginLeft: "12px", fontSize: "10px", fontWeight: 600, color: stale ? "#B45309" : "#475569" }} data-testid="share-car-liquids">
                                  🛢️ Lichide: {lq.user || "?"} · {dateStr}{stale ? " ⚠️" : " ✓"}
                                </div>
                              );
                            })()}
                            {defectLines.length > 0 && (
                              <div style={{
                                marginTop: "2px",
                                marginLeft: "12px",
                                background: "#FEF2F2",
                                borderLeft: "3px solid #DC2626",
                                padding: "4px 8px",
                                borderRadius: "3px",
                                fontSize: "11px",
                                color: "#7F1D1D",
                                fontWeight: 600,
                                lineHeight: 1.4,
                              }}>
                                {defectLines.map((dl, k) => (<div key={k}>🔧 {dl}</div>))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ fontSize: "11px", color: "#94A3B8", fontStyle: "italic" }}>—</div>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#64748B", marginBottom: "4px" }}>👷 Persoane ({names.length})</div>
                  {names.length > 0 ? (
                    <div style={{ fontSize: "12px", color: "#1E293B", lineHeight: "1.5" }}>
                      {(e.person_ids || []).filter((id) => personsIndex[id]).map((pid) => {
                        const stops = personItinerary[pid] || [];
                        const mySplit = stops.find((s) => s.entryId === e.id);
                        const multi = stops.length > 1;
                        return (
                          <div key={pid}>
                            <div>
                              • {personsIndex[pid]}
                              {mySplit?.from && (
                                <span style={{ marginLeft: "5px", fontWeight: 800, color: "#1D4ED8", fontSize: "11px" }}>
                                  ⏱ {mySplit.from}{mySplit.to ? `–${mySplit.to}` : ""}
                                </span>
                              )}
                            </div>
                            {multi && (
                              <div
                                style={{ margin: "1px 0 4px 12px", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: "3px", padding: "3px 7px", fontSize: "10.5px", color: "#1E3A8A", fontWeight: 600, lineHeight: 1.4 }}
                                data-testid={`share-itinerary-${pid}`}
                              >
                                🔀 {stops.map((s, k) => (
                                  <span key={k}>
                                    {k > 0 && <span style={{ color: "#64748B" }}> → apoi </span>}
                                    <span style={{ fontWeight: s.entryId === e.id ? 800 : 600, textDecoration: s.entryId === e.id ? "underline" : "none" }}>
                                      {s.label}{s.from ? ` (${s.from}${s.to ? `–${s.to}` : ""})` : ""}
                                    </span>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ fontSize: "11px", color: "#94A3B8", fontStyle: "italic" }}>—</div>
                  )}
                </div>
                {e.note && (
                  <div style={{
                    marginTop: "2px",
                    background: "#FEF3C7",
                    border: "1px solid #FCD34D",
                    borderRadius: "4px",
                    padding: "6px 8px",
                    fontSize: "11.5px",
                    color: "#78350F",
                    fontWeight: 600,
                    lineHeight: "1.4",
                  }}>
                    📝 {e.note}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <div style={{ textAlign: "center", padding: "30px", color: "#94A3B8", fontSize: "12px", fontStyle: "italic" }}>Nicio alocare în această zi.</div>
        )}
        {freePersons.length > 0 && (
          <div style={{ border: "2px dashed #94A3B8", borderRadius: "10px", background: "#fff", overflow: "hidden" }} data-testid="share-free-persons">
            <div style={{ background: "#475569", color: "#fff", padding: "8px 12px", fontWeight: 800, fontSize: "13px", letterSpacing: "0.3px" }}>
              🟢 LIBERI ({freePersons.length}) — disponibili, nealocați
            </div>
            <div style={{ padding: "10px 12px", fontSize: "12px", color: "#1E293B", lineHeight: "1.6", columnCount: freePersons.length > 6 ? 2 : 1, columnGap: "14px" }}>
              {freePersons.map((n, i) => (
                <div key={i} style={{ breakInside: "avoid" }}>• {n}</div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div style={{ padding: "8px 14px", background: "#0B1120", color: "rgba(255,255,255,0.6)", fontSize: "9px", textAlign: "center", letterSpacing: "0.5px" }}>
        Generat de platforma {company?.name || "ConVic"} · {(() => {
          const d = new Date();
          const dd = String(d.getDate()).padStart(2, "0");
          const mm = String(d.getMonth() + 1).padStart(2, "0");
          const HH = String(d.getHours()).padStart(2, "0");
          const MM = String(d.getMinutes()).padStart(2, "0");
          return `${dd}/${mm}/${d.getFullYear()} ${HH}:${MM}`;
        })()}
      </div>
    </div>
  );

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send size={18} className="text-green-600"/> Trimite planificare · {pretty}
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-zinc-500 mb-2">Două imagini separate: una pentru proiecte, una pentru regii. Adresa șantierului e inclusă pe fiecare card.</div>
        <div className="flex gap-2 mb-3 border-b">
          <button
            onClick={() => setActiveTab("projects")}
            className={`px-4 py-2 text-sm font-bold border-b-2 transition-colors ${activeTab === "projects" ? "border-blue-600 text-blue-700" : "border-transparent text-zinc-500 hover:text-zinc-700"}`}
            data-testid="share-tab-projects"
          >
            📋 Proiecte ({projectEntries.length})
          </button>
          <button
            onClick={() => setActiveTab("regii")}
            className={`px-4 py-2 text-sm font-bold border-b-2 transition-colors ${activeTab === "regii" ? "border-amber-600 text-amber-700" : "border-transparent text-zinc-500 hover:text-zinc-700"}`}
            data-testid="share-tab-regii"
          >
            🛠 Regii ({regieEntries.length})
          </button>
        </div>
        <div className="bg-zinc-100 rounded-md p-2 overflow-auto max-h-[55vh]">
          {/* Render both, but visually only the active one. We keep both mounted so html2canvas can capture either. */}
          <div style={{ display: activeTab === "projects" ? "block" : "none" }}>
            {renderCard(projectsRef, "Proiecte", "PROIECTE", projectEntries, "#3B82F6")}
          </div>
          <div style={{ display: activeTab === "regii" ? "block" : "none" }}>
            {renderCard(regiiRef, "Regii", "REGII", regieEntries, "#F59E0B")}
          </div>
        </div>
        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" onClick={onClose} disabled={busy}>Închide</Button>
          {projectEntries.length > 0 && regieEntries.length > 0 && (
            <Button onClick={downloadBoth} disabled={busy} variant="outline" className="border-zinc-700" data-testid="share-download-both">
              ⬇⬇ Descarcă ambele
            </Button>
          )}
          <Button
            onClick={() => downloadOne(activeTab)}
            disabled={busy || (activeTab === "projects" && projectEntries.length === 0) || (activeTab === "regii" && regieEntries.length === 0)}
            variant="outline"
            data-testid="share-download"
          >
            ⬇ Descarcă {activeTab === "projects" ? "proiecte" : "regii"}
          </Button>
          <Button
            onClick={() => shareWhatsApp(activeTab)}
            disabled={busy || (activeTab === "projects" && projectEntries.length === 0) || (activeTab === "regii" && regieEntries.length === 0)}
            className="bg-green-600 hover:bg-green-700"
            data-testid="share-whatsapp"
          >
            <Send size={14} className="mr-1.5"/> {busy ? "Se pregătește…" : "Trimite WhatsApp"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
